import React from "react";
import Footer from "../Footer";
import Navbar from "../Navbar";
import SEO from "../SEO";
import { FaUserCheck, FaShieldVirus, FaFingerprint, FaRegFileAlt, FaHistory } from "react-icons/fa";

const KYCAmlPolicy: React.FC = () => {
  const policySections = [
    {
      title: "1. Core Objective",
      icon: <FaShieldVirus />,
      content:
        "Our KYC & AML framework is designed to prevent financial misuse and ensure every transaction at EPOCH is secure, legal, and transparent. We prioritize institutional integrity to protect our users.",
    },
    {
      title: "2. Identity Verification (KYC)",
      icon: <FaFingerprint />,
      list: [
        "Digital Onboarding: Secure verification via Aadhaar, PAN, and other valid identity documents.",
        "Video KYC: Compliant, real-time verification for seamless and safe onboarding.",
        "Periodic Updates: Ensuring user information remains current and accurate over time.",
      ],
    },
    {
      title: "3. Financial Security (AML)",
      icon: <FaUserCheck />,
      list: [
        "Transaction Monitoring: Real-time flagging of unusual or high-risk financial activities.",
        "Reporting Standards: Strict adherence to FIU-IND and RBI reporting timelines.",
        "Record Integrity: Maintaining secure documentation for the mandated regulatory periods.",
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-white text-text-primary px-0 mx-0 overflow-x-hidden font-sans selection:bg-secondary/10 selection:text-secondary">
      <SEO
        title="KYC & AML Policy"
        description={`Our commitment to financial integrity. Learn about the KYC and AML standards that keep your transactions at ${import.meta.env.VITE_COMPANY_NAME} secure.`}
        keywords={`KYC policy, AML policy, financial security, fintech compliance`}
      />
      
      <div className="fixed inset-0 pointer-events-none z-0 bg-gradient-to-b from-white via-gray-50/30 to-white" />
      
      <div className="relative z-50">
        <Navbar />
      </div>

      <main className="relative z-10 pt-44 pb-20 md:pb-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          
          <header className="mb-20">
             <div className="flex items-center gap-3 px-6 py-2 bg-white border border-secondary/20 shadow-xl w-fit rounded-full mb-10 transition-transform hover:scale-105">
                <FaRegFileAlt className="text-secondary text-[10px]" />
                <span className="text-[10px] font-black tracking-[0.3em] text-text-secondary uppercase">Compliance Standards</span>
              </div>
            <h1 className="text-4xl md:text-7xl font-black tracking-tighter mb-10 text-text-primary leading-[0.9]">
              Integrity <br />
              <span className="text-secondary">& Security.</span>
            </h1>
            <p className="text-text-secondary text-lg md:text-xl font-medium leading-relaxed max-w-2xl">
              We maintain the highest standards of financial ethics. Our policies comply with the RBI Master Directions on KYC and the Prevention of Money Laundering Act (PMLA).
            </p>
          </header>

          <div className="space-y-12">
            {policySections.map((section, idx) => (
              <div key={idx} className="bg-white p-10 md:p-14 rounded-[3rem] border border-gray-100 shadow-xl relative overflow-hidden transition-all hover:shadow-2xl">
                <div className="flex flex-col md:flex-row gap-8">
                  <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center text-primary text-3xl shrink-0">
                    {section.icon}
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl md:text-3xl font-black mb-6 text-text-primary tracking-tight">
                      {section.title}
                    </h2>
                    {section.content && (
                      <p className="text-text-secondary text-lg leading-relaxed font-medium mb-6">
                        {section.content}
                      </p>
                    )}
                    {section.list && (
                      <ul className="space-y-4">
                        {section.list.map((li, i) => (
                          <li key={i} className="flex items-start gap-4 text-text-secondary font-medium text-base">
                            <div className="w-2 h-2 bg-secondary mt-2.5 rounded-full shrink-0" />
                            <span>{li}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            ))}
            
            {/* Regulatory Note */}
            <div className="bg-primary text-white p-12 md:p-16 rounded-[3rem] shadow-2xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-[80px]" />
                <h3 className="text-2xl font-black mb-6 flex items-center gap-3">
                  <FaHistory className="text-secondary text-lg" />
                  RBI Regulatory Compliance
                </h3>
                <p className="text-white/80 text-lg leading-relaxed font-medium">
                  This policy is updated as per the RBI Master Direction on KYC (August 2025). We ensure that every employee is trained and every process is audited to maintain 100% compliance with Indian financial laws.
                </p>
            </div>
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
};

export default KYCAmlPolicy;
